import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { useInstallPrompt } from "../hooks/useInstallPrompt";
import { 
  ArrowLeft,
  QrCode,
  ChartBar,
  Gear,
  ArrowSquareOut,
  Storefront,
  Download
} from "@phosphor-icons/react";
import { setOutletId, navigateToDashboard } from "../utils/appNavigation";

export default function AppSettings() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { canInstall, promptInstall } = useInstallPrompt();
  const [outlets, setOutlets] = useState([]);
  const [selectedOutlet, setSelectedOutlet] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch outlets on mount
  useEffect(() => {
    const fetchOutlets = async () => {
      try {
        const { data } = await api.get("/outlets");
        const outletsData = data || [];
        
        setOutlets(outletsData);
        
        const lastId = localStorage.getItem("ra:last_outlet_id");
        const found = outletsData.find(o => o.id === lastId);
        if (found) {
          setSelectedOutlet(found);
        } else if (outletsData.length > 0) {
          setSelectedOutlet(outletsData[0]);
          setOutletId(outletsData[0].id);
        }
      } catch (e) {
        toast.error("Could not load outlets");
      } finally {
        setLoading(false);
      }
    };
    
    fetchOutlets();
  }, []);

  // Handle outlet switch
  const handleOutletSwitch = (e) => {
    const outletId = e.target.value;
    const outlet = outlets.find(o => o.id === outletId);
    
    if (outlet) {
      setSelectedOutlet(outlet);
      setOutletId(outlet.id);
      toast.success(`Switched to ${outlet.name}`);
    }
  };

  // Navigate to app pages with outlet ID
  const goToQR = () => {
    if (!selectedOutlet) return;
    navigate(`/app/qr?outlet=${selectedOutlet.id}`);
  };

  const goToAnalytics = () => {
    if (!selectedOutlet) return;
    navigate(`/app/analytics?outlet=${selectedOutlet.id}`);
  };

  const goToConfig = () => {
    if (!selectedOutlet) return;
    navigate(`/app/outlet-config?outlet=${selectedOutlet.id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg)" }}>
        <div className="tag">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      {/* Header */}
      <header className="sticky top-0 z-10 px-5 py-4 flex items-center gap-3" style={{ background: "var(--bg)", borderBottom: "1.5px solid var(--ink)" }}>
        <button
          onClick={() => navigate("/app/redeem")}
          className="w-9 h-9 flex items-center justify-center rounded-lg transition-all hover:opacity-70"
          style={{ border: "1.5px solid var(--ink)" }}
          aria-label="Back"
        >
          <ArrowLeft size={20} weight="bold" />
        </button>
        <h1 className="display text-xl font-black">Settings</h1>
      </header>

      {/* Main Content */}
      <main className="p-5 space-y-6 max-w-xl mx-auto w-full pb-8">
        {/* Outlet Switcher */}
        <div className="card-pop p-5">
          <label className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)] mb-3 block">
            Current Outlet
          </label>
          <div className="relative">
            <select
              value={selectedOutlet?.id || ""}
              onChange={handleOutletSwitch}
              className="input-pop w-full appearance-none cursor-pointer"
            >
              {outlets.map((outlet) => (
                <option key={outlet.id} value={outlet.id}>
                  {outlet.name}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-[var(--ink)]">
              <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
              </svg>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div>
          <h2 className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)] mb-3 px-1">
            Quick Actions
          </h2>
          <div className="space-y-3">
            <button
              onClick={goToQR}
              disabled={!selectedOutlet}
              className="card-pop w-full p-4 flex items-center gap-4 transition-all hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[6px_6px_0_0_var(--ink)] disabled:opacity-50 text-left"
            >
              <div className="h-10 w-10 rounded-xl flex items-center justify-center text-white" style={{ background: "var(--pastel-blue)" }}>
                <QrCode size={20} weight="bold" color="var(--ink)" />
              </div>
              <div>
                <div className="font-bold text-[var(--ink)]">View QR Code</div>
                <div className="text-xs text-[var(--ink-muted)]">Display or download</div>
              </div>
            </button>

            <button
              onClick={goToAnalytics}
              disabled={!selectedOutlet}
              className="card-pop w-full p-4 flex items-center gap-4 transition-all hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[6px_6px_0_0_var(--ink)] disabled:opacity-50 text-left"
            >
              <div className="h-10 w-10 rounded-xl flex items-center justify-center text-white" style={{ background: "var(--secondary)" }}>
                <ChartBar size={20} weight="bold" color="var(--ink)" />
              </div>
              <div>
                <div className="font-bold text-[var(--ink)]">View Analytics</div>
                <div className="text-xs text-[var(--ink-muted)]">Track performance</div>
              </div>
            </button>

            <button
              onClick={goToConfig}
              disabled={!selectedOutlet}
              className="card-pop w-full p-4 flex items-center gap-4 transition-all hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[6px_6px_0_0_var(--ink)] disabled:opacity-50 text-left"
            >
              <div className="h-10 w-10 rounded-xl flex items-center justify-center text-white" style={{ background: "var(--pastel-yellow)" }}>
                <Gear size={20} weight="bold" color="var(--ink)" />
              </div>
              <div>
                <div className="font-bold text-[var(--ink)]">Configure Outlet</div>
                <div className="text-xs text-[var(--ink-muted)]">Edit settings</div>
              </div>
            </button>
          </div>
        </div>

        {/* Install App Option */}
        {canInstall && (
          <div className="pt-2">
            <button
              onClick={promptInstall}
              className="card-pop w-full p-4 flex items-center gap-4 transition-all hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[6px_6px_0_0_var(--ink)] text-left"
              style={{ border: "1.5px dashed var(--primary)" }}
            >
              <div className="h-10 w-10 rounded-xl flex items-center justify-center text-white" style={{ background: "var(--primary)" }}>
                <Download size={20} weight="bold" color="#fff" />
              </div>
              <div>
                <div className="font-bold text-[var(--primary)]">Install App</div>
                <div className="text-xs text-[var(--ink-muted)]">Add to home screen for quick access</div>
              </div>
            </button>
          </div>
        )}

        {/* Secondary Actions */}
        <div className="pt-4 space-y-3">
          <button
            onClick={navigateToDashboard}
            className="btn-ghost w-full justify-center"
          >
            <Storefront size={18} weight="bold" />
            Open Full Dashboard
            <ArrowSquareOut size={16} weight="bold" />
          </button>

          <button
            onClick={() => navigate("/app/redeem")}
            className="btn-primary w-full justify-center"
          >
            <ArrowLeft size={18} weight="bold" />
            Back to Redeem
          </button>
        </div>
      </main>
    </div>
  );
}